import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import DescriptionIcon from '@material-ui/icons/Description';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import CloudIcon from '@material-ui/icons/Cloud';
import AssessmentIcon from '@material-ui/icons/Assessment';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  aiQaAccessPermission,
  catalogNavAccessPermission,
  previewAccessPermission,
  releaseAccessPermission,
  releaseNotesAccessPermission,
} from '../../permissions';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, gated by SalestrakPermissionPolicy:
              dev-team       → sees everything
              team-product   → sees only Preview Envs
              team-design    → sees only Preview Envs
              everyone else  → sees nothing (default deny) */}
        <DevOnlySidebarItems />
        <ReleaseSidebarItems />
        <PreviewEnvsSidebarItem />
        <AiQaSidebarItem />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);

// Catalog/Docs/APIs/Create — gated on catalogNavAccessPermission, a basic
// permission that only dev-team is allowed for under SalestrakPermissionPolicy.
// (Must NOT use the catalog resource permission here: authorizing a resource
// permission without a resourceRef is rejected, so it always reads as denied.)
const DevOnlySidebarItems = () => {
  const catalog = usePermission({ permission: catalogNavAccessPermission });
  if (!catalog.allowed) return null;
  return (
    <>
      <SidebarItem icon={HomeIcon} to="catalog" text="Home" />
      <MyGroupsSidebarItem
        singularTitle="My Group"
        pluralTitle="My Groups"
        icon={GroupIcon}
      />
      <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
      <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
      <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
    </>
  );
};

// Renders the Release Notes / Release sidebar items only for users who
// have permission. Non-members of SalesTrak-dev see neither.
const ReleaseSidebarItems = () => {
  const releaseNotes = usePermission({ permission: releaseNotesAccessPermission });
  const release = usePermission({ permission: releaseAccessPermission });
  return (
    <>
      {releaseNotes.allowed && (
        <SidebarItem icon={DescriptionIcon} to="release-notes" text="Release Notes" />
      )}
      {release.allowed && (
        <SidebarItem icon={NewReleasesIcon} to="release" text="Release" />
      )}
    </>
  );
};

const PreviewEnvsSidebarItem = () => {
  const preview = usePermission({ permission: previewAccessPermission });
  if (!preview.allowed) return null;
  return <SidebarItem icon={CloudIcon} to="preview-envs" text="Preview Envs" />;
};

const AiQaSidebarItem = () => {
  const aiQa = usePermission({ permission: aiQaAccessPermission });
  if (!aiQa.allowed) return null;
  return <SidebarItem icon={AssessmentIcon} to="ai-qa" text="Gary" />;
};
